import { ref, watchEffect } from 'vue'

export const useFetch = async (url, config = {}, json = true) => {
  const data = ref(null);
  const error = ref(null);
  const isLoading = ref(null);

  try {
    const response = await fetch(url, { ...config });

    if (!response.ok) {
      throw response.status;
    }

    data.value = json ? await response.json() : await response.text();

  } catch(err) {
    error.value = err;
  } finally {
    isLoading.value = false;
  }

  return { error, data, isLoading }
}

const cacheMap = new Map();
const CACHE_TTL_MS = 60000;

// Entries are only TTL-checked when their own URL is re-requested, so one-off
// URLs would linger forever. Sweep expired entries on every request to keep the
// cache from growing unbounded. In-flight requests write a fresh timestamp, so
// they are never old enough to be swept.
const pruneExpired = () => {
  const now = Date.now();

  for (const [key, entry] of cacheMap) {
    if (!entry || now - entry.timestamp > CACHE_TTL_MS) cacheMap.delete(key);
  }
};

export const useCachedFetch = async (url, config = {}, json = true, overrideCache = false) => {
  const data = ref(null);
  const error = ref(null);
  const isLoading = ref(false);
  const isCached = ref(false);
  const timestamp = Date.now();

  pruneExpired();

  const cachedResponse = () => cacheMap.get(url);

  const update = () => cacheMap.set(url, { error: error, data: data, timestamp: timestamp });
  const clear = () => cacheMap.delete(url);

  if (cachedResponse() && Date.now() - cachedResponse().timestamp < CACHE_TTL_MS && !overrideCache) {
    const repsonse = cachedResponse();
    const responseData = repsonse.data;
    const responseError = repsonse.error;

    isCached.value = true;

    await awaitCachedData(responseError, responseData)

    return { error: responseError, data: responseData, isLoading, isCached }
  }

  // Expired entries are already dropped by pruneExpired(); only a still-fresh
  // entry the caller wants to bypass needs clearing here.
  if (cachedResponse() && overrideCache) {
    clear();
  }

  // Adds url key and timestamp to cacheMap for subsequent requests to find
  update();

  const fetchData = await useFetch(url, config, json);

  if (fetchData.error.value) {
    clear();
  }
  
  data.value = fetchData.data.value
  error.value = fetchData.error.value;
  isLoading.value = fetchData.isLoading.value;

  // Adds data to cacheMap
  update();

  return { error, data, isLoading, clear };
}

const awaitCachedData = (error, data) => new Promise((resolve, reject) => {
   watchEffect(() => {
    if (data.value) resolve(data);
    if (error.value) reject(error)
   })
});
